import { type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import './StatsCard.css';

/**
 * Status indicator type for sub-stats
 */
type StatusType = 'success' | 'warning' | 'danger' | 'info' | 'muted';

/**
 * Sub-stat item for displaying additional metrics
 */
export interface SubStat {
  label: string;
  value: number | string;
  status?: StatusType;
}

/**
 * StatsCard component props
 */
export interface StatsCardProps {
  /**
   * Card title
   */
  title: string;

  /**
   * Main statistic value
   */
  value: number | string;

  /**
   * Optional icon to display
   */
  icon?: ReactNode;

  /**
   * Optional sub-statistics to display below the main value
   */
  subStats?: SubStat[];

  /**
   * Loading state
   */
  isLoading?: boolean;

  /**
   * Optional click handler for card interaction
   */
  onClick?: () => void;

  /**
   * Optional additional CSS class
   */
  className?: string;
}

/**
 * StatsCard component for displaying statistics on the dashboard
 *
 * @example
 * ```tsx
 * <StatsCard
 *   title="Nodes"
 *   value={12}
 *   icon={<ServerIcon />}
 *   subStats={[
 *     { label: 'online', value: 10, status: 'success' },
 *     { label: 'offline', value: 2, status: 'danger' },
 *   ]}
 * />
 * ```
 */
export function StatsCard({
  title,
  value,
  icon,
  subStats,
  isLoading = false,
  onClick,
  className = '',
}: StatsCardProps) {
  const isClickable = !!onClick;

  const cardClasses = [
    'stats-card',
    isClickable ? 'stats-card-clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Card
      variant="bordered"
      padding={false}
      className={cardClasses}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <CardContent className="stats-card-content">
        {isLoading ? (
          <div className="stats-card-loading">
            <div className="stats-card-skeleton stats-card-skeleton-title" />
            <div className="stats-card-skeleton stats-card-skeleton-value" />
            <div className="stats-card-skeleton stats-card-skeleton-sub" />
          </div>
        ) : (
          <>
            <div className="stats-card-header">
              <span className="stats-card-title">{title}</span>
              {icon && <span className="stats-card-icon">{icon}</span>}
            </div>

            <div className="stats-card-value">{value}</div>

            {subStats && subStats.length > 0 && (
              <div className="stats-card-sub">
                {subStats.map((stat, index) => (
                  <span
                    key={index}
                    className={`stats-card-sub-item ${stat.status ? `stats-card-sub-${stat.status}` : ''}`}
                  >
                    <span className="stats-card-sub-value">{stat.value}</span>
                    <span className="stats-card-sub-label">{stat.label}</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
